import { useEffect, useState } from 'react';

declare global {
  interface Window {
    eh2: string;
    ehttd: string;
  }
}
// import PropTypes from 'prop-types';

/**
 * Object containing the logged in user, loading state, and error state.
 * @typedef {Object} IUseAuthenticate
 */
export type IUseAuthOrAnon = {
  user: User | null;
  anonymousId: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Configuration object for the useAuthenticate hook.
 * apiBaseUrl: The base URL for the API.
 * tokenParam: The query parameter used to pass the JWT token. Defaults to `token`
 * @typedef {Object} AuthConfig
 */
type AuthConfig = {
  apiBaseUrl: string;
  tokenParam: string;
};

export type User = {
  id: string;
  name: string;
  email: string;
  username: string;
  color_blind_mode: boolean;
  dark_mode: boolean;
};

const defaultConfig: AuthConfig = {
  apiBaseUrl: 'https://play.hey.gg',
  tokenParam: 'token',
};

/**
 * Custom hook for authenticating a user via a jwt token in the URL (the `token` query param) or local storage.
 * @param {AuthConfig} config The configuration object.
 * @returns {IUseAuthenticate} An object containing the user, loading state, and error state.
 * @example
 *   const ExampleComponent = () => {
 *     const { user, loading, error } = useAuthenticate({ apiBaseUrl: 'https://api.example.com' });
 *
 *     if (user) {
 *       return <div>Welcome, {user.name}!</div>
 *    }
 *   }
 */
export const useAuthOrAnon = (config: AuthConfig): IUseAuthOrAnon => {
  const localStorageUser = localStorage.getItem('heygg_user');
  const [user, setUser] = useState<User | null>(
    localStorageUser ? JSON.parse(localStorageUser as string) : null,
  );
  const [anonymousId, setAnonymousId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  config = { ...defaultConfig, ...config };

  useEffect(() => {
    const existingAnonymousId = localStorage.getItem('heygg_anonymous_id');
    const url = `${config.apiBaseUrl}/auth-or-anon${existingAnonymousId ? '?generate_anonymous_id=false' : ''}`;

    const fetchData = async () => {
      console.log('Initial storage access:', await document.hasStorageAccess());

      try {
        await document.requestStorageAccess();
        console.log(
          'Storage access after request:',
          await document.hasStorageAccess(),
        );
      } catch (e) {
        console.warn('Storage access request failed:', e);
      }

      fetch(url, {
        method: 'GET',
        credentials: 'include',
      })
        .then((response) => response.json())
        .then((data) => {
          setLoading(false);

          if (data.status === 'authenticated') {
            localStorage.setItem('heygg_jwt', data.jwt);
            localStorage.setItem('heygg_user', JSON.stringify(data.user));
            localStorage.removeItem('heygg_anonymous_id');

            // Set global window properties for authenticated user
            window.eh2 = data.user.email_eh2;
            window.ehttd = data.user.email_ehttd;

            setUser(data.user);
            setAnonymousId(null);
          } else if (data.status === 'anonymous') {
            localStorage.removeItem('heygg_jwt');
            localStorage.removeItem('heygg_user');

            if (existingAnonymousId) {
              setAnonymousId(existingAnonymousId);
            } else {
              localStorage.setItem('heygg_anonymous_id', data.anonymous_id);
              setAnonymousId(data.anonymous_id);
            }

            setUser(null);
          }
          return data;
        })
        .catch((error) => {
          setError(error.message);
          setLoading(false);
        });
    };
    fetchData();
  }, []);

  return { user, anonymousId, loading, error };
};
